import assert from 'assert';
import _ from 'underscore';

import {
  SaveToResultsStepDataName,
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {
  findAndAssertSingleStepDataFrame,
  findAndAssertStepData,
} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import {OUTPUT_NAME_SEPARATOR} from 'analysis-tool/outputs/Types';
import {InputTableApplyState} from 'analysis-tool/templates/AnalysisTemplateUtil';
import {AssaysFeature, RecipesFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL, PipelineStep, PipelineStepConfig} from 'pipelines/PipelineTypes';
import {getOutputDataForView} from 'pipelines/PipelineViewUtils';
import {getParentSteps} from 'pipelines/StepLineageUtils';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';

export function isSendToEntryEnabled(): boolean {
  return BenchlingConfig.ELN_ENABLE_ENTRY_SECTIONS;
}

export function isSendToWorksheetEnabled(): boolean {
  return (
    isFeatureEnabled({feature: AssaysFeature.ASSAYS_CREATE_ANALYSIS}) ||
    isFeatureEnabled({feature: RecipesFeature.RECIPES_CREATE_ANALYSIS})
  );
}

export function isSendToDocumentEnabled(): boolean {
  return isSendToEntryEnabled() || isSendToWorksheetEnabled();
}

/**
 * Gates the plate and file output options and the SEND_TO_DOCUMENT v1 step version, so users without
 * scratch plates never see plate or file outputs and always get the v0 step (which has neither slot).
 */
export function isScratchPlatesEnabled(): boolean {
  return BenchlingConfig.ENABLE_SCRATCH_PLATES;
}

/** SEND_TO_DOCUMENT is bumped to v1 (which adds the `Input plates` and `Input files` inputs) only for scratch plate users. */
export function getSendToDocumentStepVersion(): number {
  return isScratchPlatesEnabled() ? 1 : 0;
}

export function getOutputDataFrameName(
  template: PipelineGraphQL,
  templateStep: GetAnalysisPipelineStep,
  inputTables: Array<InputTableApplyState>,
  analysisSteps?: ReadonlyArray<PipelineStep>
): string {
  const parents = getParentSteps(template, templateStep);
  for (const parent of parents) {
    const inputTable = inputTables.find(
      (inputTable) => inputTable.templateLineageNode?.step.id === parent.id
    );
    if (inputTable) {
      if (inputTable.table == null) {
        return 'Input table';
      }
      // If the output step's direct parent is an input step, use the input table's dataframe name.
      // Use analysisSteps to resolve the view since inputTable.table comes from the user's analysis,
      // not from the template.
      const stepsForResolution = analysisSteps ?? template.steps;
      return getOutputDataForView(inputTable.table, stepsForResolution, PipelineStepDataKind.DATASET).name;
    }
  }
  const stepDataName =
    templateStep.definition.stepType === PipelineStepType.SAVE_TO_RESULTS
      ? SaveToResultsStepDataName.INPUT_DATASET
      : SINGLE_DATASET_INPUT_STEP_DATA_NAME;

  const stepData = findAndAssertStepData(templateStep, stepDataName, true);
  if (stepData.dataFrames.length === 0) {
    return 'Input table';
  }

  return findAndAssertSingleStepDataFrame(templateStep, stepDataName, true).name;
}

/**
 * Label a group of scratch plates sent to a document as "<source step>: <output slot>, <N> Plate(s)"
 * (e.g. "Import plates: Output plates, 3 Plates"); the step-name prefix is dropped when empty.
 */
export function getPlateEdgeLabel(stepName: string, fromStepDataName: string, plateCount: number): string {
  const plateNoun = plateCount === 1 ? 'Plate' : 'Plates';
  const prefix = stepName ? `${stepName}: ` : '';
  return `${prefix}${fromStepDataName}, ${plateCount} ${plateNoun}`;
}

export function getOutputNameFromOutput(
  analysisName: string,
  output: {outputName: string; parentOutputName: string | null}
): string {
  return `${_.unique(
    [analysisName].concat(output.parentOutputName ? [output.parentOutputName] : []).concat(output.outputName)
  ).join(OUTPUT_NAME_SEPARATOR)}`;
}

export function getDocumentIdFromStep(step: GetAnalysisPipelineStep): string {
  return getSendToDocumentStepConfig(step).documentId;
}

export function getSendToDocumentStepConfig(
  step: GetAnalysisPipelineStep
): PipelineStepConfig<PipelineStepType.SEND_TO_DOCUMENT, 0 | 1> {
  assert(
    step.definition.stepType === PipelineStepType.SEND_TO_DOCUMENT,
    `Expected SEND_TO_DOCUMENT_STEP, found ${step.definition.stepType}`
  );
  assert(
    step.definition.stepVersion === 0 || step.definition.stepVersion === 1,
    `Expected SEND_TO_DOCUMENT step version 0 or 1, found ${step.definition.stepVersion}`
  );
  return getPipelineStepConfig(step, PipelineStepType.SEND_TO_DOCUMENT, step.definition.stepVersion);
}
